import { FaCheck } from '@meronex/icons/fa';
import dayjs from 'dayjs';
import React from 'react';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import { useAppSelector } from '../../../../hooks/hooks';
import store from '../../../../store';
import { schedulerActions } from '../../../../store/schedulerSlice';
import { TDateFormat } from '../../../../types';
import { getDateFormat } from '../../../../utils/date';
import { SimpleButton } from '../../../common';
import { TResponsiveTableCellRenderer } from '../../../common/generic/responsiveTable/types';
import { setStage } from './mini/utils';

import { TScheduleResult, TTskSchDay } from './types';
import { ApiaUtil, IconButton } from '@apia/components';
import { arrayOrArray, getLabel, toBoolean } from '@apia/util';
import { ReserveDetails } from './ReserveDetails';
export type TSchInfoResult = {
  pro_instances: { pro_instance: TProcesses };
};
export type TProcesses = {
  id: number;
  attributes: {
    attribute: {
      title: string;
      value: string;
    }[];
  };
}[];

const dateFormat: TDateFormat = getDateFormat();
const SchedulerTableCell: TResponsiveTableCellRenderer<{
  cellId: string;
  isEnabled: boolean;
  weekDay: string;
  hour: string;
  dayNumber: number;
  value: number;
  valuePlusOverassign: number;
  isOverassignClassName: boolean;
  currentDayDefinition: TTskSchDay;
}> = ({ cell, column, row, ...props }) => {
  const isSelected = useAppSelector(
    (global) =>
      global.schedulerSlice?.selectedCell === cell.rendererProps?.cellId,
  );
  const state = useAppSelector((global) => {
    return global.schedulerSlice;
  });

  const isLoading = useAppSelector((global) => {
    return global.schedulerSlice?.isLoading !== 'NotLoading';
  });

  const onSelectDayHandler = React.useCallback(() => {
    if (isLoading) return;

    if (state.url === '/apia.control.QryRescheduleTskSchedulerAction.run') {
      store.dispatch(
        schedulerActions.setState({
          selectedCell: cell.rendererProps?.cellId,
          labelSelectedDay: {
            hour: String(cell.rendererProps?.hour),
            day: dayjs(cell.rendererProps?.weekDay, dateFormat)
              .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
              .format(dateFormat),
          },
          selectedDay: {
            hour: String(cell.rendererProps?.hour),
            day: dayjs(cell.rendererProps?.weekDay, dateFormat)
              .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
              .format(dateFormat),
            mondayWeek: String(
              dayjs(cell.rendererProps?.weekDay, dateFormat).format(dateFormat),
            ),
            dayNumber: Number(cell.rendererProps?.dayNumber),
            morningOrAfternoonSelected: Number(cell.rendererProps?.hour),
          },
        }),
      );
    } else {
      ApiaApi.get<TScheduleResult>(
        makeApiaUrl({
          ajaxUrl: '/apia.design.TaskSchedulerAction.run',
          action: 'setExecutionSchedulerSelectedDay',
          weekDay: cell.rendererProps?.weekDay,
          hour: cell.rendererProps?.hour,
          dayNumber: cell.rendererProps?.dayNumber,
        }),
      )
        .then((response) => {
          if (response?.data?.success) {
            store.dispatch(
              schedulerActions.setState({
                selectedCell: cell.rendererProps?.cellId,
                labelSelectedDay: {
                  hour: String(cell.rendererProps?.hour),
                  day: dayjs(cell.rendererProps?.weekDay, dateFormat)
                    .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
                    .format(dateFormat),
                },
                selectedDay: {
                  hour: String(cell.rendererProps?.hour),
                  day: dayjs(cell.rendererProps?.weekDay, dateFormat)
                    .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
                    .format(dateFormat),
                  mondayWeek: String(
                    dayjs(cell.rendererProps?.weekDay, dateFormat).format(
                      dateFormat,
                    ),
                  ),
                  dayNumber: Number(cell.rendererProps?.dayNumber),
                  morningOrAfternoonSelected: Number(cell.rendererProps?.hour),
                },
              }),
            );
            if (cell?.rendererProps?.currentDayDefinition) {
              setStage(
                cell.rendererProps.currentDayDefinition,
                Number(cell.rendererProps?.hour),
              );
            }
          }
        })
        .catch(() => {});
    }
  }, [
    cell.rendererProps?.cellId,
    cell.rendererProps?.currentDayDefinition,
    cell.rendererProps?.dayNumber,
    cell.rendererProps?.hour,
    cell.rendererProps?.weekDay,
    isLoading,
    state.url,
  ]);

  return (
    <td {...props} role="cell">
      <SimpleButton
        title={
          !cell.rendererProps?.isEnabled
            ? ''
            : `${dayjs(cell.rendererProps?.weekDay, dateFormat)
                .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
                .format(dateFormat)},${cell.rendererProps?.hour.slice(
                0,
                -2,
              )}:${cell.rendererProps?.hour.slice(2)}, ${String(
                cell.rendererProps?.valuePlusOverassign,
              )}
              `
        }
        aria-label={
          !cell.rendererProps?.isEnabled
            ? `${dayjs(cell.rendererProps?.weekDay, dateFormat)
                .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
                .format(dateFormat)},${
                cell.rendererProps?.hour.slice(0, -2) ?? ''
              }:${cell.rendererProps?.hour.slice(2) ?? ''}, ${String(
                'disabled',
              )}
              `
            : `${dayjs(cell.rendererProps?.weekDay, dateFormat)
                .add(Number(cell.rendererProps?.dayNumber) - 1, 'day')
                .format(dateFormat)},${cell.rendererProps?.hour.slice(
                0,
                -2,
              )}:${cell.rendererProps?.hour.slice(2)}, ${String(
                cell.rendererProps?.valuePlusOverassign,
              )}
              `
        }
        disabled={!cell.rendererProps?.isEnabled}
        {...window.getVariant('inherit')}
        onClick={onSelectDayHandler}
        className={`sched_cell ${
          cell.rendererProps?.isOverassignClassName ? 'td_overassign' : ''
        }`}
      >
        {isSelected && <FaCheck />}
        {!isSelected &&
          cell.rendererProps?.isEnabled &&
          toBoolean(state.showDisponibility) &&
          Number(cell.rendererProps?.valuePlusOverassign)}
      </SimpleButton>
      {cell.rendererProps?.currentDayDefinition.type !== 'no_laboral' &&
        ApiaUtil.instance.parsers.parseBool(
          state.showAttributes ?? 'false',
        ) && (
          <IconButton
            size={24}
            icon="Search"
            variant="transparentIcon"
            className="schd__info"
            onClick={async () => {
              const res = await ApiaApi.post<TSchInfoResult>(
                makeApiaUrl({
                  ajaxUrl: '/apia.design.TaskSchedulerAction.run',
                  action: 'getExecutionSchedulerDayInfo',
                  schId: state.data?.tskSchId,
                  weekDay: cell.rendererProps?.weekDay,
                  dayNumber: cell.rendererProps?.dayNumber,
                  hour: cell.rendererProps?.hour,
                }),
              );
              const processes = arrayOrArray(
                res?.data?.pro_instances.pro_instance,
              );

              ApiaUtil.instance.modals.open({
                title: getLabel('lblAppointmentDetails').text,
                children: <ReserveDetails processes={processes} />,
                variant: 'layout.execution.form.fields.scheduler.details',
              });
            }}
          />
        )}
    </td>
  );
};

export default SchedulerTableCell;
